import { useState, useEffect, useRef } from 'react'
import DatePickerCalendar from './DatePickerCalendar'
import { getDateRangeForPreset, formatDateRange } from '../utils/dateUtils'
import styles from './DateRangePicker.module.css'

export default function DateRangePicker({ 
    dateRange, 
    onDateRangeChange,
    className = '' 
}) {
    const [showDatePicker, setShowDatePicker] = useState(false)
    const [currentMonth, setCurrentMonth] = useState(new Date())
    const [nextMonth, setNextMonth] = useState(() => {
        const next = new Date()
        next.setMonth(next.getMonth() + 1)
        return next
    })
    const datePickerRef = useRef(null)
    
    // Date range presets
    const datePresets = [
        'Today',
        'Yesterday', 
        'This week',
        'Last week',
        'Past two weeks',
        'This month',
        'Last month',
        'This year',
        'Last year'
    ]

    // Click outside handler to close date picker
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setShowDatePicker(false)
            }
        }

        if (showDatePicker) {
            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [showDatePicker])

    // Use the centralized getDateRangeForPreset function from dateUtils

    const handleDatePresetChange = (preset) => {
        const range = getDateRangeForPreset(preset)
        onDateRangeChange({
            from: range.from,
            to: range.to,
            preset: preset
        })
        setShowDatePicker(false)
    }

    const handleDateClick = (date) => {
        const dateStr = date.toISOString().split('T')[0]
        
        if (!dateRange.from || (dateRange.from && dateRange.to)) {
            // Start new selection
            onDateRangeChange({
                from: dateStr,
                to: '',
                preset: 'Custom'
            })
        } else if (dateRange.from && !dateRange.to) {
            // Complete selection
            const fromDate = new Date(dateRange.from)
            const toDate = date
            
            if (toDate >= fromDate) {
                onDateRangeChange({
                    ...dateRange,
                    to: dateStr,
                    preset: 'Custom'
                })
            } else {
                // If end date is before start date, swap them
                onDateRangeChange({
                    from: dateStr,
                    to: dateRange.from,
                    preset: 'Custom'
                })
            }
            // Auto-dismiss dropdown when selection is complete
            setShowDatePicker(false)
        }
    }

    const navigateMonth = (direction, calendar) => {
        if (calendar === 'current') {
            const newMonth = new Date(currentMonth)
            newMonth.setMonth(currentMonth.getMonth() + direction)
            setCurrentMonth(newMonth)
            
            // Update next month to maintain side-by-side view
            const newNextMonth = new Date(newMonth)
            newNextMonth.setMonth(newMonth.getMonth() + 1)
            setNextMonth(newNextMonth)
        } else {
            const newNextMonth = new Date(nextMonth)
            newNextMonth.setMonth(nextMonth.getMonth() + direction)
            setNextMonth(newNextMonth)
            
            // Update current month to maintain side-by-side view
            const newCurrentMonth = new Date(newNextMonth)
            newCurrentMonth.setMonth(newNextMonth.getMonth() - 1)
            setCurrentMonth(newCurrentMonth)
        }
    }

    // Navigate to previous/next date range of same duration
    const navigateDateRange = (direction) => {
        if (!dateRange.from || !dateRange.to) return
        
        const fromDate = new Date(dateRange.from)
        const toDate = new Date(dateRange.to)
        const diffTime = Math.abs(toDate - fromDate)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
        
        // Calculate new dates based on direction and duration
        const newFromDate = new Date(fromDate)
        const newToDate = new Date(toDate)
        
        if (direction === 'prev') {
            newFromDate.setDate(fromDate.getDate() - diffDays)
            newToDate.setDate(toDate.getDate() - diffDays)
        } else {
            newFromDate.setDate(fromDate.getDate() + diffDays)
            newToDate.setDate(toDate.getDate() + diffDays)
        }
        
        onDateRangeChange({
            from: newFromDate.toISOString().split('T')[0],
            to: newToDate.toISOString().split('T')[0],
            preset: 'Custom'
        })
        
        // Update calendar months to show the new range
        setCurrentMonth(new Date(newFromDate.getFullYear(), newFromDate.getMonth(), 1))
        const nextMonthDate = new Date(newFromDate)
        nextMonthDate.setMonth(newFromDate.getMonth() + 1)
        setNextMonth(nextMonthDate)
    }

    // Calculate duration between from and to dates
    const calculateDuration = () => {
        if (!dateRange.from || !dateRange.to) return null
        
        const fromDate = new Date(dateRange.from)
        const toDate = new Date(dateRange.to)
        const diffTime = Math.abs(toDate - fromDate)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
        
        if (diffDays === 1) {
            return '1 day'
        } else if (diffDays === 7) {
            return '1 week'
        } else if (diffDays === 14) {
            return '2 weeks'
        } else if (diffDays >= 28 && diffDays <= 31) {
            return '1 month'
        } else if (diffDays >= 365 && diffDays <= 366) {
            return '1 year'
        } else if (diffDays < 7) {
            return `${diffDays} days`
        } else if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7)
            const remainingDays = diffDays % 7
            if (remainingDays === 0) {
                return `${weeks} week${weeks > 1 ? 's' : ''}`
            } else {
                return `${weeks} week${weeks > 1 ? 's' : ''} ${remainingDays} day${remainingDays > 1 ? 's' : ''}`
            }
        } else if (diffDays < 365) {
            const months = Math.floor(diffDays / 30)
            const remainingDays = diffDays % 30
            if (remainingDays === 0) {
                return `${months} month${months > 1 ? 's' : ''}`
            } else {
                return `${months} month${months > 1 ? 's' : ''} ${remainingDays} day${remainingDays > 1 ? 's' : ''}`
            }
        } else {
            const years = Math.floor(diffDays / 365)
            const remainingDays = diffDays % 365
            if (remainingDays === 0) {
                return `${years} year${years > 1 ? 's' : ''}`
            } else {
                return `${years} year${years > 1 ? 's' : ''} ${remainingDays} day${remainingDays > 1 ? 's' : ''}`
            }
        }
    }

    const formatDateRangeDisplay = () => {
        if (dateRange.preset && dateRange.preset !== 'Custom') {
            return dateRange.preset
        }
        if (dateRange.from && dateRange.to) {
            if (dateRange.from === dateRange.to) {
                return formatDateRange(dateRange.from, dateRange.from)
            }
            return formatDateRange(dateRange.from, dateRange.to)
        }
        return 'Select date range'
    }

    return (
        <div className={`${styles.dateRangeContainer} ${className}`}>
            {/* Previous range button */}
            <button 
                className={styles.rangeNavButton}
                onClick={() => navigateDateRange('prev')}
                disabled={!dateRange.from || !dateRange.to}
                title="Previous period"
            >
                ‹
            </button>
            
            <div className={styles.dateRangeWrapper} ref={datePickerRef}>
                <button 
                    className={styles.dateRangeButton}
                    onClick={() => setShowDatePicker(!showDatePicker)}
                >
                    <span className={styles.calendarIcon}>📅</span>
                    <span>{formatDateRangeDisplay()}</span>
                    <span className={styles.dropdownArrow}>▼</span>
                </button>
                
                {showDatePicker && (
                    <div className={styles.datePickerDropdown}>
                        <div className={styles.datePickerContent}>
                            {/* Left side - Date presets */}
                            <div className={styles.datePresets}>
                                <div className={styles.presetsHeader}>
                                    <h4>Quick Select</h4>
                                </div>
                                {datePresets.map(preset => (
                                    <button
                                        key={preset}
                                        className={`${styles.datePresetItem} ${dateRange.preset === preset ? styles.active : ''}`}
                                        onClick={() => handleDatePresetChange(preset)}
                                    >
                                        {preset}
                                    </button>
                                ))}
                            </div>
                            
                            {/* Right side - Mini calendars */}
                            <div className={styles.calendarSection}>
                                <div className={styles.calendarContainer}>
                                    <DatePickerCalendar
                                        monthDate={currentMonth}
                                        calendarId="current"
                                        dateRange={dateRange}
                                        onDateClick={handleDateClick}
                                        onNavigateMonth={navigateMonth}
                                    />
                                    <DatePickerCalendar
                                        monthDate={nextMonth}
                                        calendarId="next"
                                        dateRange={dateRange}
                                        onDateClick={handleDateClick}
                                        onNavigateMonth={navigateMonth}
                                    />
                                </div>
                                
                                {/* Duration display */}
                                {calculateDuration() && (
                                    <div className={styles.durationDisplay}>
                                        {calculateDuration()}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Next range button */}
            <button 
                className={styles.rangeNavButton}
                onClick={() => navigateDateRange('next')}
                disabled={!dateRange.from || !dateRange.to}
                title="Next period"
            >
                ›
            </button>
        </div>
    )
}